import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  FolderZip as ZipIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import ProjectPageShell from '../ProjectDashboardPage/ProjectPageShell';
import AuthorChip from '../../components/AuthorChip';
import DocumentUploadDialog from '../../components/dialogs/DocumentUploadDialog';
import {
  documentFileVisual,
  formatDocumentSize,
} from '../../components/dialogs/DocumentUploadDialog/fileVisual';
import {
  documentBaseName,
  documentFolder,
  filesFromDrop,
} from '../../components/dialogs/DocumentUploadDialog/folderFiles';
import usePermissions from '../../hooks/usePermissions';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABEL,
  documentCategoryLabel,
  SiteDocument,
} from '../../models/SiteDocument';
import { SiteDocumentService } from '../../services/SiteDocumentService';

interface Snack {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

type SortKey = 'recent' | 'name' | 'size';

const SORT_LABEL: Record<SortKey, string> = {
  recent: '최신 업로드순',
  name: '파일명순',
  size: '큰 파일순',
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
};

/** 카테고리 컬럼 도입 전 문서는 빈 값으로 오므로 'other' 로 취급해 분류에서 누락되지 않게 한다. */
const catOf = (doc: SiteDocument): string => (doc.category?.trim() ? doc.category : 'other');

/**
 * ProjectPageShell 과 같은 출처(localStorage)에서 현장 이름을 읽는다.
 * 어느 현장의 보관함인지 화면에 확실히 보여 주기 위한 것 — 현장별로 파일이
 * 완전히 분리돼 있는데도 화면에 현장명이 없으면 사용자가 확신할 수 없다.
 */
const readSelectedSiteName = (): string | null => {
  try {
    const raw = localStorage.getItem('selectedProject');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { name?: string; name_ko?: string };
    return parsed.name_ko?.trim() || parsed.name?.trim() || null;
  } catch {
    return null;
  }
};

/** 파일명으로 쓸 수 없는 문자를 걷어낸다 (ZIP 저장명). */
const safeFilePart = (s: string): string => s.replace(/[\\/:*?"<>|]/g, '_').trim();

/**
 * 현장 파일 보관함 페이지.
 *
 * 사이드바 '파일 보관함' 메뉴에서 진입. 현장별로 임의 형식 파일을 공유한다.
 * 분류 탭 · 검색 · 정렬로 찾고, 여러 건을 선택해 ZIP 으로 한 번에 받거나 지운다.
 * 업로드는 DocumentUploadDialog(파일별 설명·분류 + 병렬 전송)에 맡긴다.
 */
const SiteDocumentsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = projectId ? Number(projectId) : NaN;
  const validId = Number.isFinite(projectIdNum);
  const { canEdit } = usePermissions();

  const [docs, setDocs] = useState<SiteDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [zipping, setZipping] = useState(false);
  const [snack, setSnack] = useState<Snack>({ open: false, message: '', severity: 'success' });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[] | undefined>();

  const [category, setCategory] = useState<string>('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('recent');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const siteName = useMemo(readSelectedSiteName, []);

  const reload = useCallback(async () => {
    if (!validId) return;
    setLoading(true);
    try {
      // 분류 탭마다 개수를 보여 주려면 전체가 필요하므로 서버 필터 없이 받아
      // 화면에서 걸러 낸다 (현장 단위 목록이라 규모가 크지 않다).
      const data = await SiteDocumentService.list(projectIdNum);
      setDocs(data);
      // 사라진 파일이 선택에 남아 있으면 ZIP/삭제가 조용히 어긋난다.
      setSelected((prev) => {
        const alive = new Set(data.map((d) => d.id));
        return new Set(Array.from(prev).filter((id) => alive.has(id)));
      });
    } catch (e) {
      setSnack({
        open: true,
        message: e instanceof Error ? e.message : '파일 목록 조회 실패',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [projectIdNum, validId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    DOCUMENT_CATEGORIES.forEach((k) => {
      c[k] = 0;
    });
    docs.forEach((d) => {
      const k = catOf(d);
      c[k] = (c[k] ?? 0) + 1;
    });
    return c;
  }, [docs]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = docs.filter((d) => {
      if (category && catOf(d) !== category) return false;
      if (!q) return true;
      return (
        d.original_name.toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q) ||
        (d.created_by_name || '').toLowerCase().includes(q)
      );
    });
    const sorted = [...filtered];
    if (sort === 'name') {
      sorted.sort((a, b) => a.original_name.localeCompare(b.original_name, 'ko'));
    } else if (sort === 'size') {
      sorted.sort((a, b) => (b.size_bytes || 0) - (a.size_bytes || 0));
    }
    // 'recent' 는 서버가 이미 최신순으로 준다.
    return sorted;
  }, [docs, category, query, sort]);

  const selectedVisible = visible.filter((d) => selected.has(d.id));
  const allVisibleSelected = visible.length > 0 && selectedVisible.length === visible.length;

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((d) => next.delete(d.id));
      else visible.forEach((d) => next.add(d.id));
      return next;
    });
  };

  const handleDownload = async (doc: SiteDocument) => {
    setDownloadingId(doc.id);
    try {
      await SiteDocumentService.download(doc);
    } catch (e) {
      setSnack({
        open: true,
        message: e instanceof Error ? e.message : '다운로드 실패',
        severity: 'error',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleZip = async () => {
    const ids = selectedVisible.map((d) => d.id);
    if (ids.length === 0) return;
    setZipping(true);
    try {
      const label = safeFilePart(siteName || `현장${projectIdNum}`);
      await SiteDocumentService.downloadZip(projectIdNum, ids, `${label}_파일_${ids.length}건.zip`);
      setSnack({ open: true, message: `${ids.length}개 파일을 묶어 받았습니다`, severity: 'success' });
    } catch (e) {
      setSnack({
        open: true,
        message: e instanceof Error ? e.message : '묶음 다운로드 실패',
        severity: 'error',
      });
    } finally {
      setZipping(false);
    }
  };

  const handleDelete = async (doc: SiteDocument) => {
    if (!window.confirm(`'${doc.original_name}' 파일을 삭제하시겠습니까?`)) return;
    try {
      await SiteDocumentService.delete(doc.id);
      setSnack({ open: true, message: '삭제되었습니다', severity: 'success' });
      await reload();
    } catch (e) {
      setSnack({ open: true, message: e instanceof Error ? e.message : '삭제 실패', severity: 'error' });
    }
  };

  const handleBulkDelete = async () => {
    const targets = selectedVisible;
    if (targets.length === 0) return;
    if (!window.confirm(`선택한 ${targets.length}개 파일을 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;
    const failed: string[] = [];
    for (const doc of targets) {
      try {
        await SiteDocumentService.delete(doc.id);
      } catch {
        failed.push(doc.original_name);
      }
    }
    await reload();
    setSnack(
      failed.length === 0
        ? { open: true, message: `${targets.length}개를 삭제했습니다`, severity: 'success' }
        : { open: true, message: `${failed.length}개 삭제 실패: ${failed.join(', ')}`, severity: 'error' },
    );
  };

  const openUpload = (files?: File[]) => {
    setDroppedFiles(files);
    setUploadOpen(true);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (!canEdit) return;
    // 폴더를 놓아도 안쪽 파일까지 담기도록 항목을 훑는다(다이얼로그와 같은 경로).
    filesFromDrop(e.dataTransfer).then((files) => {
      if (files.length) openUpload(files);
    });
  };

  return (
    <ProjectPageShell
      projectId={projectId ?? ''}
      activeSection="documents"
      breadcrumbExtra={
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
          <DescriptionIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          파일 보관함
        </Typography>
      }
      hideSettingsButton
    >
      {!validId ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography color="error">유효하지 않은 프로젝트 ID</Typography>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            outline: dragActive ? '2px dashed' : 'none',
            outlineColor: 'primary.main',
            outlineOffset: -4,
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (canEdit) setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
        >
          {/* 헤더 — 어느 현장의 보관함인지 확실히 보여 준다. */}
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{
              px: 2.5,
              py: 1.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexWrap: 'wrap',
              rowGap: 1,
            }}
          >
            <Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" fontWeight={700}>
                  파일 보관함
                </Typography>
                {siteName && <Chip size="small" color="primary" variant="outlined" label={siteName} />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {loading ? '불러오는 중…' : `총 ${docs.length}개`} · 이 현장에서만 보이고 현장 구성원 모두가
                받을 수 있습니다
                {canEdit ? ' · 파일을 끌어다 놓아 업로드' : ''}
              </Typography>
            </Stack>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => openUpload(undefined)}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                파일 업로드
              </Button>
            )}
          </Stack>

          {/* 분류 탭 */}
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              px: 2.5,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexWrap: 'wrap',
              rowGap: 0.75,
            }}
          >
            <Chip
              size="small"
              label={`전체 (${docs.length})`}
              color={category === '' ? 'primary' : 'default'}
              variant={category === '' ? 'filled' : 'outlined'}
              onClick={() => setCategory('')}
            />
            {DOCUMENT_CATEGORIES.map((c) => (
              <Chip
                key={c}
                size="small"
                label={`${DOCUMENT_CATEGORY_LABEL[c]} (${counts[c] ?? 0})`}
                color={category === c ? 'primary' : 'default'}
                variant={category === c ? 'filled' : 'outlined'}
                onClick={() => setCategory(c)}
              />
            ))}
          </Stack>

          {/* 검색 · 정렬 · 선택 동작 */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 2.5,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexWrap: 'wrap',
              rowGap: 1,
            }}
          >
            <TextField
              size="small"
              placeholder="파일명 · 설명 · 업로더 검색"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: query ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQuery('')} aria-label="검색어 지우기">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <TextField
              select
              size="small"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              sx={{ minWidth: 150 }}
              aria-label="정렬"
            >
              {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
                <MenuItem key={k} value={k}>
                  {SORT_LABEL[k]}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ flex: 1 }} />

            {selectedVisible.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary">
                  {selectedVisible.length}개 선택
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={zipping ? <CircularProgress size={14} color="inherit" /> : <ZipIcon />}
                  onClick={handleZip}
                  disabled={zipping}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {zipping ? '압축 중…' : 'ZIP 다운로드'}
                </Button>
                {canEdit && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBulkDelete}
                    sx={{ textTransform: 'none' }}
                  >
                    선택 삭제
                  </Button>
                )}
              </>
            )}
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : docs.length === 0 ? (
            <Box sx={{ py: 7, textAlign: 'center', color: 'text.secondary' }}>
              <DescriptionIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                보관된 파일이 없습니다.
              </Typography>
              <Typography variant="caption">
                {canEdit
                  ? '우측 상단 "파일 업로드" 버튼 또는 드래그 앤 드롭으로 추가하세요.'
                  : '편집자 또는 관리자만 업로드할 수 있습니다.'}
              </Typography>
            </Box>
          ) : visible.length === 0 ? (
            <Box sx={{ py: 7, textAlign: 'center', color: 'text.secondary' }}>
              <SearchIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                조건에 맞는 파일이 없습니다.
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  setQuery('');
                  setCategory('');
                }}
                sx={{ textTransform: 'none', mt: 0.5 }}
              >
                필터 초기화
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allVisibleSelected}
                        indeterminate={selectedVisible.length > 0 && !allVisibleSelected}
                        onChange={toggleAllVisible}
                        inputProps={{ 'aria-label': '전체 선택' }}
                      />
                    </TableCell>
                    <TableCell>파일 / 설명</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>분류</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>크기</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>업로더</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>업로드일</TableCell>
                    <TableCell align="right">동작</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((doc) => {
                    const { Icon, color } = documentFileVisual(doc.original_name, doc.mime_type);
                    return (
                      <TableRow key={doc.id} hover selected={selected.has(doc.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selected.has(doc.id)}
                            onChange={() => toggleOne(doc.id)}
                            inputProps={{ 'aria-label': `${doc.original_name} 선택` }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                            <Icon sx={{ color, flexShrink: 0, mt: 0.25 }} fontSize="small" />
                            <Stack sx={{ minWidth: 0 }}>
                              <Tooltip title={doc.original_name}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: 420,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleDownload(doc)}
                                >
                                  {documentBaseName(doc.original_name)}
                                </Typography>
                              </Tooltip>
                              {documentFolder(doc.original_name) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    maxWidth: 420,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  📁 {documentFolder(doc.original_name)}
                                </Typography>
                              )}
                              {doc.description ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}
                                >
                                  {doc.description}
                                </Typography>
                              ) : (
                                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                  설명 없음
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip size="small" variant="outlined" label={documentCategoryLabel(doc.category)} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                          {formatDocumentSize(doc.size_bytes)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <AuthorChip name={doc.created_by_name} userId={doc.created_by_id} label="" dense />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                          {formatDate(doc.created_at)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="다운로드">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(doc)}
                                disabled={downloadingId === doc.id}
                                aria-label={`${doc.original_name} 다운로드`}
                              >
                                {downloadingId === doc.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canEdit && (
                            <Tooltip title="삭제">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(doc)}
                                aria-label={`${doc.original_name} 삭제`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <DocumentUploadDialog
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setDroppedFiles(undefined);
        }}
        projectId={validId ? projectIdNum : undefined}
        projectLabel={siteName ?? undefined}
        initialFiles={droppedFiles}
        onUploaded={(n) => {
          setSnack({ open: true, message: `${n}개 파일을 업로드했습니다`, severity: 'success' });
          void reload();
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </ProjectPageShell>
  );
};

export default SiteDocumentsPage;
